import {arraysMatch, insertAfter, setValueAtIndex} from '@/helpers/array.js'
import {useThrottleFn} from '@vueuse/core'
import {ref, unref, computed, isRef, watch} from 'vue'

/**
 * Querying composable
 */
export function useQuerying(options) {

  //Must have handler
  if (typeof options.handler !== 'function') {
    throw new Error(`Must provide at least a handler function`)
  }

  //Refs
  const items = ref([])
  const total = ref(0)
  const offset = ref(0)
  const limit = ref(unref(options.limit) || 100)
  const sort = ref(unref(options.sort) || '')
  const search = ref('')
  const filter = ref(unref(options.filter) || {})
  const hasItems = computed(() => items.value.length > 0)
  const isLoadingItems = ref(false)
  const isLoadingMoreItems = ref(false)
  const hasLoadedItems = ref(false) //First time load
  const hasLoadedAllItems = computed(() => items.value.length >= total.value)

  //Get query params
  const getQueryParams = () => {
    return Object.assign({}, filter.value || {}, {
      sort: sort.value || undefined,
      offset: offset.value || undefined,
      limit: limit.value || undefined,
      search: search.value || undefined,
    })
  }

  //Item querying (does not update items ref)
  const queryItems = async (queryParams, flatArray = false) => {

    //Get response
    const response = await options.handler(queryParams)
    const {meta, items} = response

    //Return as flat array?
    if (flatArray) {
      return items
    }

    //Return items and meta data
    return {items, meta}
  }

  //Item loader (does update items ref)
  const loadItems = async () => {
    try {

      //Query items
      const queryParams = getQueryParams()
      const response = await queryItems(queryParams)

      //Set data
      items.value = response.items
      total.value = response.meta.total
      offset.value = response.meta.offset
    }
    finally {
      isLoadingItems.value = false
      hasLoadedItems.value = true
    }
  }

  //Reload data
  const reloadItems = () => {
    items.value = []
    offset.value = 0
    isLoadingItems.value = true
    loadItems()
  }

  //Load page handler
  const loadItemsForPage = async page => {
    page = unref(page)
    offset.value = (page - 1) * limit.value
    isLoadingItems.value = true
    loadItems()
  }

  //Throtted load more handler
  const loadMoreItems = useThrottleFn(async () => {

    //Everything's been loaded
    if (hasLoadedAllItems.value) {
      return
    }

    //Load more items
    try {

      //Set flag and increase offset
      isLoadingMoreItems.value = true
      offset.value += limit.value

      //Query items
      const queryParams = getQueryParams()
      const response = await queryItems(queryParams)

      //Set new items value
      items.value = [
        ...items.value,
        ...response.items,
      ]

      //Set meta data
      total.value = response.meta.total
      offset.value = response.meta.offset
    }
    finally {
      isLoadingMoreItems.value = false
      hasLoadedItems.value = true
    }
  }, 250)

  //Change limit
  const changeLimit = (newLimit, reload = true) => {
    newLimit = unref(newLimit)
    if (limit.value === newLimit) {
      return
    }
    limit.value = newLimit
    if (reload) {
      reloadItems()
    }
  }

  //Sort handler
  const changeSort = (newSort, reload = true) => {
    newSort = unref(newSort)
    if (sort.value === newSort) {
      return
    }
    sort.value = newSort
    if (reload) {
      reloadItems()
    }
  }

  //Search handler
  const changeSearch = (newSearch, reload = true) => {
    newSearch = unref(newSearch)
    if (search.value === newSearch) {
      return
    }
    search.value = newSearch
    if (reload) {
      reloadItems()
    }
  }

  //Clear search
  const clearSearch = (reload = true) => {
    changeSearch('', reload)
  }

  //Set filter
  const setFilter = (newFilter, reload = true) => {
    newFilter = unref(newFilter) || {}
    if (isFilterActive(newFilter)) {
      return
    }
    filter.value = newFilter
    if (reload) {
      reloadItems()
    }
  }

  //Reset filter to default
  const resetFilter = (reload = true) => {
    setFilter(options.filter, reload)
  }

  //Change filter
  const changeFilter = (filterObj, reload = true) => {
    const newFilter = Object.assign({}, filter.value, unref(filterObj))
    setFilter(newFilter, reload)
  }

  //Check if a specific filter is currently active
  const isFilterActive = filterObj => {
    if (Object.keys(filterObj).length !== Object.keys(filter.value).length) {
      return false
    }
    return Object.keys(filterObj).every(key => {
      return filterObj[key] === filter.value[key]
    })
  }

  //Check if a filter value is empty
  const isEmptyFilterValue = value => {
    return (
      value === null ||
      value === undefined ||
      (Array.isArray(value) && value.length === 0)
    )
  }

  //Check if a filter value is the same as the default value
  const isDefaultFilterValue = (value, defaultValue) => {
    if (Array.isArray(value) && Array.isArray(defaultValue)) {
      return arraysMatch(value, defaultValue)
    }
    return value === defaultValue
  }

  //Check how many (non-default) filters are active
  const numActiveFilters = computed(() => {
    let num = 0
    for (const key in filter.value) {

      //Default value specified for this filter key
      if (options.filter && typeof options.filter[key] !== 'undefined') {
        if (!isDefaultFilterValue(filter.value[key], options.filter[key])) {
          num++
        }
      }

      //No default value specified, simply check if value is empty
      else if (!isEmptyFilterValue(filter.value[key])) {
        num++
      }
    }
    return num
  })

  //Manually add an item to the list (e.g. when creating a new item)
  const addItem = (item, afterItem)  => {
    items.value = insertAfter(items.value, afterItem, item)
    total.value++
  }

  //Manually remove an item from the list (e.g. when deleting an item)
  const removeItem = item => {
    items.value = items.value.filter(i => i.id !== item.id)
    total.value--
  }

  //Manually replace an item in the list
  const replaceItem = newItem => {
    const index = items.value.findIndex(i => i.id === newItem.id)
    if (index >= 0) {
      items.value = setValueAtIndex(items.value, index, newItem)
    }
  }

  //Watch for default filter changes
  if (isRef(options.filter)) {
    watch(options.filter, () => resetFilter())
  }

  //Expose interface
  return {

    //Refs
    items,
    total,
    offset,
    limit,
    sort,
    search,
    filter,
    numActiveFilters,
    hasItems,
    isLoadingItems,
    isLoadingMoreItems,
    hasLoadedItems,
    hasLoadedAllItems,

    //Methods
    queryItems,
    loadItems,
    loadItemsForPage,
    loadMoreItems,
    reloadItems,
    changeLimit,
    changeSort,
    changeSearch,
    changeFilter,
    clearSearch,
    resetFilter,
    isFilterActive,
    getQueryParams,
    addItem,
    removeItem,
    replaceItem,
  }
}
